(() => {
  'use strict';

  const root = document.documentElement;
  const routesRoot = window.Shopify?.routes?.root || '/';
  const select = (selector, context = document) => context.querySelector(selector);
  const selectAll = (selector, context = document) => Array.from(context.querySelectorAll(selector));

  const currency = window.Shopify?.currency?.active || 'USD';
  const formatMoney = (cents) => {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        currencyDisplay: 'symbol'
      }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const escapeHtml = (value) => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  let toastTimer;
  const showToast = (message, type = 'success') => {
    let toast = select('[data-theme-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'theme-toast';
      toast.setAttribute('data-theme-toast', '');
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }

    toast.dataset.type = type;
    toast.innerHTML = `<span>${escapeHtml(message)}</span><button type="button" aria-label="Dismiss">×</button>`;
    toast.classList.add('is-visible');
    select('button', toast)?.addEventListener('click', () => toast.classList.remove('is-visible'), { once: true });
    clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 3600);
  };

  const updateCartCount = async () => {
    try {
      const response = await fetch(`${routesRoot}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      selectAll('[data-cart-count]').forEach((badge) => {
        badge.textContent = cart.item_count;
        badge.hidden = cart.item_count === 0;
      });
    } catch (error) {
      // Keep the native cart flow functional if the AJAX refresh fails.
    }
  };

  const addToCart = async (form, button) => {
    if (!form || !button || button.disabled) return;
    const buttonText = select('span', button);
    const previousText = buttonText?.textContent || button.textContent;
    button.disabled = true;
    button.classList.add('is-loading');
    if (buttonText) buttonText.textContent = 'Adding…';

    try {
      const response = await fetch(`${routesRoot}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this item.');
      await updateCartCount();
      showToast(`${result.product_title || 'Item'} added to your cart.`);
      document.dispatchEvent(new CustomEvent('novacart:cart-updated', { detail: result }));
    } catch (error) {
      showToast(error.message || 'Unable to add this item.', 'error');
    } finally {
      button.classList.remove('is-loading');
      button.disabled = false;
      if (buttonText) buttonText.textContent = previousText;
    }
  };

  const initMobileNavigation = () => {
    const drawer = select('[data-mobile-drawer]');
    const openButton = select('[data-mobile-menu-open]');
    const closeButton = select('[data-mobile-menu-close]');
    const overlay = select('[data-mobile-overlay]');
    if (!drawer || !openButton) return;

    const close = () => {
      drawer.classList.remove('is-open');
      overlay?.classList.remove('is-visible');
      drawer.setAttribute('aria-hidden', 'true');
      window.setTimeout(() => { drawer.hidden = true; if (overlay) overlay.hidden = true; }, 220);
      openButton.setAttribute('aria-expanded', 'false');
      root.classList.remove('drawer-open');
      openButton.focus({ preventScroll: true });
    };
    const open = () => {
      drawer.hidden = false;
      if (overlay) overlay.hidden = false;
      window.requestAnimationFrame(() => {
        drawer.classList.add('is-open');
        overlay?.classList.add('is-visible');
      });
      drawer.setAttribute('aria-hidden', 'false');
      openButton.setAttribute('aria-expanded', 'true');
      root.classList.add('drawer-open');
      closeButton?.focus({ preventScroll: true });
    };

    openButton.addEventListener('click', open);
    closeButton?.addEventListener('click', close);
    overlay?.addEventListener('click', close);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    selectAll('[data-mobile-submenu-toggle]', drawer).forEach((button) => {
      button.addEventListener('click', () => {
        const submenu = button.nextElementSibling;
        const expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!expanded));
        submenu?.toggleAttribute('hidden', expanded);
      });
    });
  };

  const initQuantitySelectors = () => {
    document.addEventListener('click', (event) => {
      const minus = event.target.closest('[data-quantity-minus]');
      const plus = event.target.closest('[data-quantity-plus]');
      if (!minus && !plus) return;
      const selector = event.target.closest('[data-quantity-selector]');
      const input = select('input[type="number"]', selector);
      if (!input) return;
      const min = Number(input.min || 1);
      const max = Number(input.max || Number.POSITIVE_INFINITY);
      const step = Number(input.step || 1);
      const current = Number(input.value || min);
      input.value = String(Math.min(max, Math.max(min, current + (plus ? step : -step))));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  };

  const initProductGalleries = () => {
    selectAll('[data-product-gallery]').forEach((gallery) => {
      selectAll('[data-media-target]', gallery).forEach((button) => {
        button.addEventListener('click', () => {
          const mediaId = button.dataset.mediaTarget;
          if (!mediaId) return;
          selectAll('[data-media-target]', gallery).forEach((candidate) => candidate.classList.toggle('is-active', candidate.dataset.mediaTarget === mediaId));
          selectAll('[data-media-id]', gallery).forEach((media) => {
            const active = media.dataset.mediaId === mediaId;
            media.classList.toggle('is-active', active);
            media.hidden = !active;
            if (!active) selectAll('video', media).forEach((video) => video.pause());
          });
        });
      });

      select('[data-product-zoom]', gallery)?.addEventListener('click', () => {
        const zoomed = gallery.classList.toggle('is-zoomed');
        root.classList.toggle('media-zoom-open', zoomed);
      });
      gallery.addEventListener('click', (event) => {
        if (gallery.classList.contains('is-zoomed') && event.target.closest('.product-media')) {
          gallery.classList.remove('is-zoomed');
          root.classList.remove('media-zoom-open');
        }
      });
    });
  };

  const variantMatches = (variant, selectedOptions) => variant.options.every((option, index) => option === selectedOptions[index]);

  const updateProductPrice = (container, variant) => {
    const priceNode = select('[data-product-price]', container);
    if (!priceNode || !variant) return;
    const sale = Number(variant.compare_at_price || 0) > Number(variant.price || 0);
    const saved = sale ? Number(variant.compare_at_price) - Number(variant.price) : 0;
    priceNode.innerHTML = `
      <div class="price${sale ? ' price--on-sale' : ''}">
        <span class="price__current">${formatMoney(variant.price)}</span>
        ${sale ? `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s><span class="price__save">Save ${formatMoney(saved)}</span>` : ''}
      </div>`;
  };

  const initProductForms = () => {
    selectAll('[data-product-section]').forEach((section) => {
      const form = select('[data-product-form]', section);
      const variantsNode = select('[data-product-variants]', section);
      const variantSelect = select('[data-variant-select]', form);
      if (!form || !variantsNode || !variantSelect) return;

      let variants = [];
      try {
        variants = JSON.parse(variantsNode.textContent);
      } catch (error) {
        variants = [];
      }

      const updateVariant = () => {
        const selectedOptions = selectAll('[data-product-option]', form).map((fieldset) => {
          const checked = select('[data-option-value]:checked', fieldset);
          select('[data-selected-option]', fieldset).textContent = checked?.value || '';
          return checked?.value || '';
        });
        const variant = variants.find((candidate) => variantMatches(candidate, selectedOptions)) || variants.find((candidate) => String(candidate.id) === variantSelect.value);
        if (!variant) return;

        variantSelect.value = variant.id;
        const addButton = select('[data-add-to-cart]', form);
        const addText = select('span', addButton);
        addButton.disabled = !variant.available;
        if (addText) addText.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
        const stock = select('.product-stock', section);
        if (stock) {
          stock.classList.toggle('is-unavailable', !variant.available);
          const strong = select('strong', stock);
          if (strong) strong.textContent = variant.available ? 'In stock' : 'Sold out';
        }
        updateProductPrice(section, variant);

        if (variant.featured_media?.id) {
          select(`[data-media-target="${variant.featured_media.id}"]`, section)?.click();
        }

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());

        selectAll('[data-sticky-variant]', document).forEach((input) => { input.value = variant.id; });
        selectAll('[data-sticky-price]', document).forEach((price) => { price.textContent = formatMoney(variant.price); });
        selectAll('[data-sticky-variant-title]', document).forEach((title) => { title.textContent = variant.public_title || variant.title || ''; });
      };

      selectAll('[data-option-value]', form).forEach((input) => input.addEventListener('change', updateVariant));
      variantSelect.addEventListener('change', updateVariant);
      form.addEventListener('submit', (event) => {
        const submitter = event.submitter || select('[data-add-to-cart]', form);
        if (!submitter || submitter.name !== 'add') return;
        event.preventDefault();
        addToCart(form, submitter);
      });
    });
  };

  const initQuickAdd = () => {
    selectAll('[data-quick-add-form]').forEach((form) => {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const button = event.submitter || select('button[type="submit"]', form);
        addToCart(form, button);
      });
    });
  };

  const getWishlist = () => {
    try {
      return JSON.parse(window.localStorage.getItem('novacart-wishlist') || '[]');
    } catch (error) {
      return [];
    }
  };

  const initWishlist = () => {
    const updateButtons = () => {
      const wishlist = getWishlist();
      selectAll('[data-wishlist]').forEach((button) => {
        const active = wishlist.includes(button.dataset.wishlist);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      selectAll('[data-wishlist-count]').forEach((node) => {
        node.textContent = wishlist.length;
        node.hidden = wishlist.length === 0;
      });
    };

    selectAll('[data-wishlist-open]').forEach((button) => {
      button.addEventListener('click', () => {
        const count = getWishlist().length;
        showToast(count ? `${count} item${count === 1 ? '' : 's'} saved in this browser.` : 'Your wishlist is empty.');
      });
    });

    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-wishlist]');
      if (!button) return;
      event.preventDefault();
      const key = button.dataset.wishlist;
      if (!key) return;
      const wishlist = getWishlist();
      const index = wishlist.indexOf(key);
      if (index >= 0) {
        wishlist.splice(index, 1);
        showToast('Removed from your wishlist.');
      } else {
        wishlist.push(key);
        showToast('Saved to your wishlist.');
      }
      window.localStorage.setItem('novacart-wishlist', JSON.stringify(wishlist));
      updateButtons();
    });
    updateButtons();
  };

  const initCountdowns = () => {
    selectAll('[data-countdown]').forEach((timer) => {
      let endTime;
      const configured = timer.dataset.countdown;
      if (configured) endTime = new Date(configured).getTime();
      if (!Number.isFinite(endTime)) {
        const storageKey = `novacart-countdown-${timer.dataset.countdownId || 'default'}`;
        endTime = Number(window.sessionStorage.getItem(storageKey));
        if (!endTime || endTime < Date.now()) {
          endTime = Date.now() + 8 * 60 * 60 * 1000;
          window.sessionStorage.setItem(storageKey, String(endTime));
        }
      }

      const render = () => {
        const remaining = Math.max(0, endTime - Date.now());
        const totalSeconds = Math.floor(remaining / 1000);
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        const values = { days, hours, minutes, seconds };
        Object.entries(values).forEach(([key, value]) => {
          const node = select(`[data-${key}]`, timer);
          if (node) node.textContent = String(value).padStart(2, '0');
        });
      };
      render();
      window.setInterval(render, 1000);
    });
  };

  const initCollectionControls = () => {
    selectAll('[data-filter-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        const target = select('[data-filter-sidebar]', button.closest('[data-catalog-layout]') || document);
        if (!target) return;
        const open = target.classList.toggle('is-open');
        button.setAttribute('aria-expanded', String(open));
        root.classList.toggle('facets-open', open);
      });
    });
    selectAll('[data-facets-close]').forEach((button) => {
      button.addEventListener('click', () => {
        const target = button.closest('[data-filter-sidebar]');
        target?.classList.remove('is-open');
        root.classList.remove('facets-open');
        selectAll('[data-filter-toggle]').forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
      });
    });

    selectAll('[data-sort-select]').forEach((selectNode) => {
      selectNode.addEventListener('change', () => {
        const url = new URL(window.location.href);
        if (selectNode.value) url.searchParams.set('sort_by', selectNode.value);
        else url.searchParams.delete('sort_by');
        window.location.assign(url.toString());
      });
    });

    selectAll('[data-grid-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        const mode = button.dataset.gridToggle;
        const grid = select('[data-product-grid]');
        if (!grid) return;
        grid.classList.toggle('product-grid--list', mode === 'list');
        selectAll('[data-grid-toggle]').forEach((candidate) => {
          candidate.classList.toggle('is-active', candidate === button);
          candidate.setAttribute('aria-pressed', String(candidate === button));
        });
        window.localStorage.setItem('novacart-grid-mode', mode);
      });
    });
    const storedMode = window.localStorage.getItem('novacart-grid-mode');
    if (storedMode) select(`[data-grid-toggle="${storedMode}"]`)?.click();
  };

  const initTabs = () => {
    selectAll('[data-tabs]').forEach((tabs) => {
      const buttons = selectAll('[data-tab-target]', tabs);
      const panels = selectAll('[data-tab-panel]', tabs);
      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          const target = button.dataset.tabTarget;
          buttons.forEach((candidate) => {
            const active = candidate === button;
            candidate.classList.toggle('is-active', active);
            candidate.setAttribute('aria-selected', String(active));
          });
          panels.forEach((panel) => {
            const active = panel.dataset.tabPanel === target;
            panel.classList.toggle('is-active', active);
            panel.hidden = !active;
          });
        });
      });
    });
  };

  const initStickyAddToCart = () => {
    selectAll('[data-sticky-atc]').forEach((bar) => {
      const productSection = select('[data-product-section]');
      const mainForm = select('[data-product-form]', productSection);
      const mainButton = select('[data-add-to-cart]', mainForm);
      if (!productSection || !mainForm || !mainButton) return;

      const observer = new IntersectionObserver(([entry]) => {
        bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }, { threshold: 0.05 });
      observer.observe(mainButton);

      const stickyQuantity = select('[data-sticky-quantity]', bar);
      const mainQuantity = select('input[name="quantity"]', mainForm);
      const syncQuantity = () => {
        if (stickyQuantity && mainQuantity) mainQuantity.value = String(Math.max(1, Number(stickyQuantity.value || 1)));
      };
      select('[data-sticky-minus]', bar)?.addEventListener('click', () => {
        if (!stickyQuantity) return;
        stickyQuantity.value = String(Math.max(1, Number(stickyQuantity.value || 1) - 1));
        syncQuantity();
      });
      select('[data-sticky-plus]', bar)?.addEventListener('click', () => {
        if (!stickyQuantity) return;
        stickyQuantity.value = String(Math.max(1, Number(stickyQuantity.value || 1) + 1));
        syncQuantity();
      });
      stickyQuantity?.addEventListener('change', syncQuantity);
      select('[data-sticky-add]', bar)?.addEventListener('click', () => {
        syncQuantity();
        addToCart(mainForm, mainButton);
      });
    });
  };

  const initCartUtilities = () => {
    selectAll('[data-coupon-form]').forEach((form) => {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const input = select('input', form);
        const code = input?.value.trim();
        if (!code) {
          showToast('Enter a discount code.', 'error');
          input?.focus();
          return;
        }
        window.location.assign(`${routesRoot}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(`${routesRoot}cart`)}`);
      });
    });
  };

  const initHeaderSearch = () => {
    selectAll('[data-search-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        const panel = select('[data-header-search]');
        if (!panel) return;
        const open = panel.classList.toggle('is-open');
        button.setAttribute('aria-expanded', String(open));
        if (open) select('input[type="search"]', panel)?.focus();
      });
    });
  };

  const initDetailsGroups = () => {
    selectAll('[data-exclusive-details]').forEach((group) => {
      selectAll('details', group).forEach((details) => {
        details.addEventListener('toggle', () => {
          if (!details.open) return;
          selectAll('details', group).forEach((other) => {
            if (other !== details) other.open = false;
          });
        });
      });
    });
  };

  const init = () => {
    initMobileNavigation();
    initHeaderSearch();
    initQuantitySelectors();
    initProductGalleries();
    initProductForms();
    initQuickAdd();
    initWishlist();
    initCountdowns();
    initCollectionControls();
    initTabs();
    initStickyAddToCart();
    initCartUtilities();
    initDetailsGroups();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', init);
})();
